import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import type { TrendStrategy, MediaPromptSuggestion } from './types';

/**
 * Social listening (Glystn) feeding content strategy (Gemini).
 *
 * The two halves are deliberately independent: media prompts can be generated
 * from a typed topic even when trend mining is unavailable, so a missing
 * Glystn key degrades this tab rather than disabling it.
 */

interface TrendsComponentProps {
  onUsePrompt?: (prompt: string, mediaType: 'image' | 'video') => void;
  onUseCaption?: (caption: string, hashtags: string[], mediaPrompt?: string, mediaType?: 'image' | 'video') => void;
}

const SUGGESTED_TOPICS = [
  'zero proof cocktails',
  'espresso martini',
  'sober curious',
  'non alcoholic drinks',
  'mocktail recipes',
];

const TrendsComponent: React.FC<TrendsComponentProps> = ({ onUsePrompt, onUseCaption }) => {
  const [topic, setTopic] = useState('');
  const [mode, setMode] = useState<'semantic' | 'keyword'>('semantic');

  const [strategy, setStrategy] = useState<TrendStrategy | null>(null);
  const [mediaPrompts, setMediaPrompts] = useState<MediaPromptSuggestion[]>([]);

  const [isMining, setIsMining] = useState(false);
  const [isPrompting, setIsPrompting] = useState(false);
  const [trendsEnabled, setTrendsEnabled] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const notify = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 6000);
  };

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiService.getTrendStatus() as unknown as {
        status: { trendMiningEnabled: boolean };
      };
      setTrendsEnabled(res.status.trendMiningEnabled);
    } catch {
      // Assume enabled; the mine call will surface the real error.
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleMine = async () => {
    if (!topic.trim()) return;
    setIsMining(true);
    setStrategy(null);

    try {
      const res = await apiService.generateTrendStrategy(topic, mode) as unknown as {
        strategy: TrendStrategy;
      };
      setStrategy(res.strategy);
      notify('success', 'Strategy generated from live social data.');
    } catch (error) {
      notify('error', error instanceof Error ? error.message : 'Trend mining failed.');
    } finally {
      setIsMining(false);
    }
  };

  const handleGeneratePrompts = async () => {
    if (!topic.trim()) return;
    setIsPrompting(true);

    try {
      // Feed the mined summary through when we have one, so prompts are
      // grounded in the trend rather than the topic string alone.
      const context = strategy
        ? `${strategy.summary}\n\n${strategy.themes.map((t) => `${t.theme}: ${t.opportunity}`).join('\n')}`
        : '';

      const res = await apiService.generateMediaPrompts(topic, context) as unknown as {
        prompts: MediaPromptSuggestion[];
      };
      setMediaPrompts(res.prompts || []);
      notify('success', `${res.prompts?.length || 0} prompts generated.`);
    } catch (error) {
      notify('error', error instanceof Error ? error.message : 'Prompt generation failed.');
    } finally {
      setIsPrompting(false);
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`rounded-xl px-6 py-4 ${
          message.type === 'success'
            ? 'bg-scarf-green/10 border border-scarf-green/30 text-scarf-green'
            : 'bg-brand-red/10 border border-brand-red/30 text-brand-red'
        }`}>
          <p className="font-body">{message.text}</p>
        </div>
      )}

      {!trendsEnabled && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-xl px-6 py-4">
          <p className="font-body text-yellow-800">
            Social listening is unavailable — <code className="font-mono text-sm">GLYSTN_API_KEY</code> is
            not set on the server. You can still generate media prompts from a topic below.
          </p>
        </div>
      )}

      {/* Topic input */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-2">Mine Social Trends</h3>
        <p className="text-gray-600 font-body text-sm mb-4">
          Searches real social posts and transcripts, then turns what it finds into a content plan.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleMine(); }}
            placeholder="e.g. zero proof cocktails"
            className="flex-1 px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy placeholder-gray-400 focus:border-brand-blue outline-none font-body"
          />
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as 'semantic' | 'keyword')}
            className="px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy font-body"
          >
            <option value="semantic">Semantic</option>
            <option value="keyword">Exact keyword</option>
          </select>
          <button
            onClick={handleMine}
            disabled={isMining || !topic.trim() || !trendsEnabled}
            className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
          >
            {isMining ? 'Mining…' : 'Mine Trends'}
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {SUGGESTED_TOPICS.map((t) => (
            <button
              key={t}
              onClick={() => setTopic(t)}
              className="text-sm bg-frost-white hover:bg-ice-teal/20 text-deep-navy px-3 py-1.5 rounded-full border border-ice-teal-light transition-colors font-body"
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Strategy */}
      {strategy && (
        <>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-3">What&apos;s Happening</h3>
            <p className="text-deep-navy/80 font-body leading-relaxed">{strategy.summary}</p>
          </div>

          {strategy.themes.length > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Themes</h3>
              <div className="space-y-4">
                {strategy.themes.map((t, i) => (
                  <div key={i} className="bg-frost-white rounded-xl p-4">
                    <p className="font-bold text-deep-navy font-body">{t.theme}</p>
                    <p className="text-sm text-gray-600 font-body mt-1">
                      <span className="font-bold">Evidence:</span> {t.evidence}
                    </p>
                    <p className="text-sm text-brand-blue font-body mt-1">
                      <span className="font-bold">Opportunity:</span> {t.opportunity}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {strategy.recommendations.length > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Post Ideas</h3>
              <div className="space-y-4">
                {strategy.recommendations.map((r, i) => (
                  <div key={i} className="bg-frost-white rounded-xl p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-bold text-deep-navy font-body">{r.title}</p>
                        <span className="inline-block text-xs bg-ice-teal/30 text-deep-navy px-2 py-0.5 rounded mt-1 font-body">
                          {r.contentTheme}
                        </span>
                      </div>
                      {onUseCaption && (
                        <button
                          onClick={() => onUseCaption(
                            r.suggestedCaption,
                            r.suggestedHashtags || [],
                            r.mediaPrompt,
                            r.mediaType,
                          )}
                          className="flex-shrink-0 bg-brand-blue hover:bg-brand-blue-dark text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors"
                        >
                          Use in composer
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 font-body mt-2">{r.rationale}</p>
                    <p className="text-deep-navy/80 font-body mt-3 italic">&ldquo;{r.suggestedCaption}&rdquo;</p>
                    {r.suggestedHashtags?.length > 0 && (
                      <p className="text-sm text-brand-blue font-body mt-2">
                        {r.suggestedHashtags.map((h) => `#${h}`).join(' ')}
                      </p>
                    )}
                    {r.mediaPrompt && (
                      <div className="mt-3 bg-white rounded-lg p-3 border border-ice-teal-light">
                        <p className="text-xs font-bold text-gray-600 font-body mb-1">
                          {r.mediaType === 'video' ? 'REEL' : 'IMAGE'} PROMPT
                        </p>
                        <p className="text-sm text-deep-navy/80 font-mono">{r.mediaPrompt}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Media prompts */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h3 className="text-xl font-title font-bold text-deep-navy">Media Prompts</h3>
            <p className="text-gray-600 font-body text-sm mt-1">
              {strategy
                ? 'Grounded in the trends mined above.'
                : 'Enter a topic above. Mining first makes these sharper, but is not required.'}
            </p>
          </div>
          <button
            onClick={handleGeneratePrompts}
            disabled={isPrompting || !topic.trim()}
            className="flex-shrink-0 bg-brand-blue-light hover:bg-brand-blue text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
          >
            {isPrompting ? 'Generating…' : 'Generate Prompts'}
          </button>
        </div>

        {mediaPrompts.length > 0 && (
          <div className="space-y-3 mt-4">
            {mediaPrompts.map((p, i) => (
              <div key={i} className="bg-frost-white rounded-xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-bold text-deep-navy font-body">
                      {p.label}
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded font-body ${
                        p.mediaType === 'video'
                          ? 'bg-brand-red/15 text-brand-red'
                          : 'bg-ice-teal/30 text-deep-navy'
                      }`}>
                        {p.mediaType === 'video' ? 'REEL' : 'IMAGE'}
                      </span>
                    </p>
                    <p className="text-sm text-gray-600 font-body mt-1">{p.rationale}</p>
                  </div>
                  {onUsePrompt && (
                    <button
                      onClick={() => onUsePrompt(p.prompt, p.mediaType)}
                      className="flex-shrink-0 bg-brand-blue hover:bg-brand-blue-dark text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors"
                    >
                      Use prompt
                    </button>
                  )}
                </div>
                <p className="text-deep-navy/80 font-mono text-sm mt-3 bg-white rounded-lg p-3 border border-ice-teal-light">
                  {p.prompt}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendsComponent;
